import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callAPI } from '../../../utils/apiUtils';
import { apiUrls } from '../../../utils/apiUrls';

export const notificationKeys = {
  all: ['notifications'],
  details: () => [...notificationKeys.all, 'detail'],
  detail: (id) => [...notificationKeys.details(), id],
  unread: () => [...notificationKeys.all, 'unread'],
  count: () => [...notificationKeys.all, 'count'],
};

const fetchNotifications = async (page = 1, limit = 10) => {
  const response = await callAPI(
    apiUrls.notificationGet,
    { page, limit },
    'GET',
    {}
  );

  if (response?.data?.success) {
    return {
      notifications: response.data.data.getData || [],
      count: response.data.data.count || 0,
      unreadCount: response.data.data.unreadCount || 0,
      totalPages: response.data.data.totalPages || 0,
    };
  }
  throw new Error(response?.data?.message || 'Failed to fetch notifications');
};

const markAsReadAPI = async (ids) => {
  const response = await callAPI(
    `${apiUrls.notificationIsRead}`,
    {},
    'POST',
    {ids}
  );

  if (response?.data?.success) {
    return response.data.data;
  }
  throw new Error(response?.data?.message || 'Failed to mark notification as read');
};

export const useNotifications = (page = 1, limit = 10, options = {}) => {
  return useQuery({
    queryKey: [...notificationKeys.all, page, limit],
    queryFn: () => fetchNotifications(page, limit),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    ...options,
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsReadAPI,
    onSuccess: (data, ids) => {
      if (ids === '') {
        queryClient.setQueryData(notificationKeys.all, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            notifications: oldData.notifications.map(notification => ({
              ...notification,
              isRead: true
            })),
            unreadCount: 0
          };
        });
      } else {
        queryClient.setQueryData(notificationKeys.all, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            notifications: oldData.notifications.map(notification =>
              notification._id === ids
                ? { ...notification, isRead: true }
                : notification
            ),
            unreadCount: Math.max(0, (oldData.unreadCount || 0) - 1)
          };
        });
      }

      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: (error) => {
      console.error('Failed to mark notification as read:', error.message);
    },
  });
};

export const useNotificationDropdown = () => {
  const queryClient = useQueryClient();

  const {
    data = { notifications: [], count: 0, unreadCount: 0, totalPages: 0 },
    isLoading,
    error
  } = useNotifications(1, 10);

  const markAsReadMutation = useMarkAsRead();

  const handleMarkAsRead = (notificationId) => {
    return markAsReadMutation.mutateAsync(notificationId);
  };

  const handleMarkAllAsRead = () => {
    return markAsReadMutation.mutateAsync('');
  };

  const refreshNotifications = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  };

  return {
    notifications: data.notifications || [],
    count: data.count || 0,
    unreadCount: data.unreadCount || 0,
    totalPages: data.totalPages || 0,
    isLoading,
    error,
    handleMarkAsRead,
    handleMarkAllAsRead,
    refreshNotifications,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAsReadMutation.isPending && markAsReadMutation.variables === '',
  };
};